import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { environment } from '@g99-product-web-ui/shared-environment';
import { ToastService } from '@g99-product-web-ui/shared-service';
import {
  DeleteIcon,
  ExportIcon,
  PlusIconSvg,
} from '@g99-product-web-ui/shared-util-lib';

import { MultiFileUploadService } from './multi-file-upload.service';

export interface UploadedFile {
  progress: number;
  previewUrl?: string;
  file?: File; // Make it optional
  filename?: string;
  contentType?: string;
  type: string;
  isPreviewOnly?: boolean; // NEW: Flag to indicate this is a preview, not an upload
  imageRatio?: ImageRatio;
}

export type ImageRatio = '1:1' | '4:5';

export interface IFileUploadEvent {
  files: File[];
  isMaxFilesSizeExceeded: boolean;
  isValid: boolean;
  imageRatio?: ImageRatio;
}

@Component({
  selector: 'lib-multi-file-upload',
  templateUrl: './multi-file-upload.component.html',
  styleUrl: './multi-file-upload.component.scss',
  standalone: false,
})
export class MultiFileUploadComponent implements OnChanges {
  @ViewChild('fileInput') public fileInput!: ElementRef<HTMLInputElement>;

  @Output() public filesUploaded = new EventEmitter<IFileUploadEvent>();
  /** Emits the preview that was removed, so callers showing several previews know which one. */
  @Output() public previewDeleted = new EventEmitter<UploadedFile>();
  @Input() public existingFiles: any[] = []; // Accepts existing file data from parent
  @Input() public maxFiles = 5;
  @Input() public disableFileDrop = false;
  @Input() public allowedFileTypes: string[] = []; // Can accept MIME types or extensions
  @Input() public showLargePreview = false; // NEW: Flag to show large preview for images

  @Input() public addMaxFileSizeValidation = false;
  @Input() public maxFileSizeMB = 2; // Default max file size in MB
  @Input() public maxVideoSizeMB = 100; // Default max video size in MB

  @Input() public maxTotalSizeMB?: number; // Optional max total size in MB
  @Input() public readOnly = false;
  @Input() public processInstagramImageRatio = false;

  protected uploadedFiles: UploadedFile[] = [];
  protected uploadLogo = ExportIcon;
  protected addIcon = PlusIconSvg;
  protected deleteIcon = DeleteIcon;
  protected isDragging = false;

  // Property for size tracking
  protected totalSize = 0; // in bytes
  protected Math = Math;
  protected supportedFileTypesUrl =
    environment.production.SUPPORTED_FILE_TYPES_URL;

  constructor(
    private readonly _toastService: ToastService,
    private readonly _fileUploadService: MultiFileUploadService
  ) {}

  protected get totalFilesCount(): number {
    return this.uploadedFiles.length + this.existingFiles.length;
  }

  // NEW: Check if we have actual uploaded files (not just previews)
  protected get hasActualUploadedFiles(): boolean {
    return this.uploadedFiles.some((file) => !file.isPreviewOnly);
  }

  // NEW: Get only preview files
  protected get previewFiles(): UploadedFile[] {
    return this.uploadedFiles.filter((file) => file.isPreviewOnly);
  }

  // NEW: Get only actual uploaded files
  protected get actualUploadedFiles(): UploadedFile[] {
    return this.uploadedFiles.filter((file) => !file.isPreviewOnly);
  }

  ngOnChanges(): void {
    if (this.existingFiles && this.existingFiles.length > 0) {
      // Clear previous preview files
      this.uploadedFiles = this.uploadedFiles.filter(
        (file) => !file.isPreviewOnly
      );

      // Add new preview files
      const previewFiles = this.existingFiles.map((file) => {
        // Extract filename from location URL if not provided
        let filename = file?.filename || file?.fileName || file?.name;
        if (!filename && (file?.location || file?.fileUrl)) {
          // Extract filename from URL
          const url = file?.location || file?.fileUrl;
          const urlParts = url.split('/');
          filename = urlParts[urlParts.length - 1] || 'uploaded-file';
        }

        // Map contentType to type if needed
        const fileType = file?.type || file?.contentType || '';

        return {
          filename: filename || 'uploaded-file',
          previewUrl: file?.location || file?.fileUrl || '', // Use existing URL for preview
          progress: 100, // Mark as fully uploaded
          type: fileType,
          file: file?.size ? file : undefined, // Only set file if it has size
          isPreviewOnly: true, // NEW: Mark as preview
        };
      });

      // Add preview files to the beginning
      this.uploadedFiles = [...previewFiles, ...this.uploadedFiles];
      this._calculateTotalSize();
    } else {
      // Clear preview files if no existing files
      this.uploadedFiles = this.uploadedFiles.filter(
        (file) => !file.isPreviewOnly
      );
    }
  }

  public removeUnsupportedFiles(): void {
    if (!this.uploadedFiles.length) {
      return;
    }
    const allowedTypes = this.allowedFileTypes.map((t) =>
      t.toLowerCase().replace(/^\./, '')
    );
    const unsupportedFiles = this.uploadedFiles.filter((fileData) => {
      const fileName = fileData.file?.name || '';
      const ext = fileName.split('.').pop()?.toLowerCase() || '';
      return !allowedTypes.includes(ext);
    });

    if (unsupportedFiles.length > 0) {
      // Remove all unsupported files
      this.uploadedFiles = this.uploadedFiles.filter((fileData) => {
        const fileName = fileData.file?.name || '';
        const ext = fileName.split('.').pop()?.toLowerCase() || '';
        return allowedTypes.includes(ext);
      });

      this._emitUploadedFiles();
    }
  }

  /**
   * Opens file picker with accepted types
   */
  public openFilePicker(acceptedTypes: string[]): void {
    if (this.fileInput) {
      this.fileInput.nativeElement.accept = acceptedTypes?.join(',') || '*';
      this.fileInput.nativeElement.click();
    }
  }

  /**
   * Generate proper accept attribute for file input
   */
  protected getAcceptAttribute(): string {
    return this._fileUploadService.getAcceptAttribute(this.allowedFileTypes);
  }

  /**
   * Handles file selection event
   */
  protected hasImageFiles(): boolean {
    return this.uploadedFiles.some((file) => this._isImageFile(file));
  }

  protected hasVideoFiles(): boolean {
    return this.uploadedFiles.some((file) => this._isVideoFile(file));
  }

  /**
   * Handles file selection event
   */
  protected async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const rawFiles = Array.from(input.files);

    // ✅ Step 1: Filter by allowed file types FIRST
    const { validFiles, rejectedFiles } =
      this._fileUploadService.filterFilesByType(
        rawFiles,
        this.allowedFileTypes
      );

    if (rejectedFiles.length > 0) {
      const rejectedNames = rejectedFiles.map((f) => f.name).join(', ');
      const allowedTypesDisplay =
        this._fileUploadService.getAllowedTypesDisplay(this.allowedFileTypes);

      this._toastService.showError(
        `File(s) rejected: ${rejectedNames}. Only ${allowedTypesDisplay} files are allowed.`
      );
    }

    if (validFiles.length === 0) {
      input.value = ''; // Reset input field
      return;
    }

    const finalFilesToUse: File[] = [];

    for (const file of validFiles) {
      if (this._isInputVideo(file)) {
        // Video size validation: must be checked immediately
        const maxSizeBytes = this.maxVideoSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
          this._toastService.showError(
            `Video size exceeds the maximum allowed limit of ${this.maxVideoSizeMB} MB. Please upload a smaller video.`
          );
          continue;
        }

        // Validate duration (max 3 minutes / 180 seconds)
        try {
          const duration = await this._getVideoDuration(file);
          if (duration > 180) {
            this._toastService.showError(
              'Video duration exceeds the maximum allowed limit of 3 minutes. Please upload a shorter video.'
            );
            continue;
          }
        } catch (err) {
          console.error('Failed to load video metadata:', err);
          this._toastService.showError(
            'Could not read video metadata. Please ensure the file is a valid MP4 or MOV video.'
          );
          continue;
        }

        // Check if there is already an image in existing files or in the final files batch
        if (
          this.hasImageFiles() ||
          finalFilesToUse.some((f) => this._isInputImage(f))
        ) {
          this._toastService.showError('Video cannot be combined with images.');
          continue;
        }

        // Check if there is already a video in existing files or in the final files batch
        if (
          this.hasVideoFiles() ||
          finalFilesToUse.some((f) => this._isInputVideo(f))
        ) {
          this._toastService.showError('Only one video is allowed per post.');
          continue;
        }

        // Check slot availability
        const currentCount =
          this.uploadedFiles.length +
          this.existingFiles.length +
          finalFilesToUse.length;
        if (currentCount >= 1) {
          this._toastService.showError('Only one video is allowed per post.');
          continue;
        }

        finalFilesToUse.push(file);
      } else if (this._isInputImage(file)) {
        // Check if there is already a video in existing files or in the final files batch
        if (
          this.hasVideoFiles() ||
          finalFilesToUse.some((f) => this._isInputVideo(f))
        ) {
          this._toastService.showError(
            'Images cannot be combined with a video.'
          );
          continue;
        }

        // Image size validation (keep existing logic):
        if (this.addMaxFileSizeValidation) {
          if (file.size > this.maxFileSizeMB * 1024 * 1024) {
            this._toastService.showError(
              `Some files exceed the maximum size of ${this.maxFileSizeMB}MB and were skipped.`
            );
            continue;
          }
        }

        // Check slot availability
        const currentCount =
          this.uploadedFiles.length +
          this.existingFiles.length +
          finalFilesToUse.length;
        if (currentCount >= this.maxFiles) {
          this._toastService.showError(
            `You have already reached the maximum of ${this.maxFiles} ${this.maxFiles === 1 ? 'file' : 'files'}.`
          );
          continue;
        }

        finalFilesToUse.push(file);
      } else {
        // For other files, treat like images or general files
        if (
          this.hasVideoFiles() ||
          finalFilesToUse.some((f) => this._isInputVideo(f))
        ) {
          this._toastService.showError(
            'Files cannot be combined with a video.'
          );
          continue;
        }
        if (this.addMaxFileSizeValidation) {
          if (file.size > this.maxFileSizeMB * 1024 * 1024) {
            this._toastService.showError(
              `Some files exceed the maximum size of ${this.maxFileSizeMB}MB and were skipped.`
            );
            continue;
          }
        }
        const currentCount =
          this.uploadedFiles.length +
          this.existingFiles.length +
          finalFilesToUse.length;
        if (currentCount >= this.maxFiles) {
          this._toastService.showError(
            `You have already reached the maximum of ${this.maxFiles} ${this.maxFiles === 1 ? 'file' : 'files'}.`
          );
          continue;
        }
        finalFilesToUse.push(file);
      }
    }

    if (finalFilesToUse.length === 0) {
      input.value = ''; // Reset input field
      return;
    }

    for (const file of finalFilesToUse) {
      let processedFile = file;
      let imageRatio: ImageRatio | undefined;

      if (this.processInstagramImageRatio && this._isImage(file)) {
        try {
          const result =
            await this._fileUploadService.processInstagramImage(file);

          processedFile = result.file;
          imageRatio = result.imageRatio;
        } catch {
          // Keep the original file if image processing fails
        }
      }

      const fileData: UploadedFile = {
        file: processedFile,
        progress: 0,
        type: processedFile.type,
        filename: processedFile.name,
        isPreviewOnly: false,
        imageRatio,
      };

      // Generate preview if image
      if (this._isImage(processedFile)) {
        fileData.previewUrl =
          this._fileUploadService.createFilePreview(processedFile);
      }

      this.uploadedFiles.push(fileData);
      this._fileUploadService.simulateUploadProgress(fileData);
    }

    this._calculateTotalSize();
    this._emitUploadedFiles();
    input.value = ''; // Reset input field
  }

  /**
   * Removes file from the uploaded list
   * Uses the file reference to find the correct index in uploadedFiles,
   * so it works correctly for both previewFiles and actualUploadedFiles.
   */
  protected removeFile(fileToRemove: UploadedFile): void {
    const index = this.uploadedFiles.indexOf(fileToRemove);
    if (index === -1) {
      return;
    }

    const removedFile = this.uploadedFiles[index];
    this.uploadedFiles.splice(index, 1);

    if (removedFile.isPreviewOnly) {
      // Preview was removed - notify parent to clear preview
      this.previewDeleted.emit(removedFile);
      // Still emit files event with empty array
      this._emitUploadedFiles();
    } else {
      // Actual uploaded file was removed
      this._emitUploadedFiles();
    }

    this._calculateTotalSize();
  }

  /**
   * Check if total size exceeds the limit
   */
  protected isOverSizeLimit(): boolean {
    if (!this.maxTotalSizeMB) {
      return false;
    }
    return this.totalSize > this.maxTotalSizeMB * 1024 * 1024;
  }

  /**
   * Get files that should be highlighted as removable (when over limit)
   */
  protected isFileRemovable(fileData: UploadedFile): boolean {
    return this.isOverSizeLimit() && (fileData.file?.size || 0) > 1024 * 1024; // Files over 1MB
  }

  /**
   * Check if upload is disabled due to size limit or max files reached
   */
  protected isUploadDisabled(): boolean {
    const currentTotalFiles =
      this.uploadedFiles.length + this.existingFiles.length;
    return this.isOverSizeLimit() || currentTotalFiles >= this.maxFiles;
  }

  protected triggerAddMoreFiles(): void {
    if (this.fileInput && !this.isUploadDisabled()) {
      this.fileInput.nativeElement.click();
    }
  }

  protected onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  protected onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  protected onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (this.disableFileDrop || this.isUploadDisabled()) {
      return;
    }

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      this.handleDroppedFiles(droppedFiles);
    }
  }

  protected handleDroppedFiles(files: File[] | FileList) {
    // Convert files (array or FileList) into a real FileList using DataTransfer
    try {
      const dataTransfer = new DataTransfer();
      if (files instanceof FileList) {
        for (let i = 0; i < files.length; i++) {
          const file = files.item(i);
          if (file) {
            dataTransfer.items.add(file);
          }
        }
      } else {
        for (const f of files) {
          dataTransfer.items.add(f);
        }
      }

      const event = {
        target: {
          files: dataTransfer.files,
        },
      } as any;

      this.onFileSelected(event);
    } catch (e) {
      // Fallback: if DataTransfer is not available (e.g., some environments), pass an array or FileList-like
      const event = {
        target: {
          files: Array.isArray(files) ? files : Array.from(files as FileList),
        },
      } as any;
      this.onFileSelected(event);
    }
  }

  /**
   * Calculate total size
   */
  private _calculateTotalSize(): void {
    this.totalSize = this.uploadedFiles.reduce(
      (total, file) => total + (file.file?.size || 0),
      0
    );
  }

  /**
   * Emits uploaded files to the parent component
   * NEW: Only emits actual uploaded files, not previews
   */
  private _emitUploadedFiles(): void {
    const actualFiles = this.uploadedFiles.filter(
      (fileData) => !fileData.isPreviewOnly
    );

    const files = actualFiles
      .map((fileData) => fileData.file)
      .filter((file): file is File => !!file);

    const imageFile = actualFiles.find((fileData) => fileData.imageRatio);

    this.filesUploaded.emit({
      files,
      isMaxFilesSizeExceeded: this.isOverSizeLimit(),
      isValid: files.length > 0 && !this.isOverSizeLimit(),
      imageRatio: imageFile?.imageRatio,
    });
  }

  /**
   * Checks if the file is an image
   */
  private _isImage(file: File): boolean {
    return file.type.startsWith('image/');
  }

  private _isImageFile(file: UploadedFile): boolean {
    const type = file.contentType || file.type || file.file?.type || '';
    const name = file.filename || file.file?.name || '';
    return type.startsWith('image/') || /\.(jpg|jpeg|png|webp)$/i.test(name);
  }

  private _isVideoFile(file: UploadedFile): boolean {
    const type = file.contentType || file.type || file.file?.type || '';
    const name = file.filename || file.file?.name || '';
    return type.startsWith('video/') || /\.(mp4|mov)$/i.test(name);
  }

  private _isInputImage(file: File): boolean {
    return (
      file.type.startsWith('image/') ||
      /\.(jpg|jpeg|png|webp)$/i.test(file.name)
    );
  }

  private _isInputVideo(file: File): boolean {
    return file.type.startsWith('video/') || /\.(mp4|mov)$/i.test(file.name);
  }

  private _getVideoDuration(file: File): Promise<number> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };
      video.src = URL.createObjectURL(file);
    });
  }
}
